import { call } from '@advanza/api'
import { SelectSimple } from '@advanza/input'
import { SelectSimpleChangeEvent } from '@advanza/input/interfaces/SelectSimpleChangeEvent'
import { Button } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import InputField from '@trustoo/components/input/InputField'
import {
    buildKeywordMappingFilter,
    isPageFullySelected,
    KeywordMappingBulkAction,
    KeywordMappingBulkFilter,
    KeywordMappingBulkTarget,
    keywordMappingFilterSignature,
    KeywordMappingListFilter,
    readAppliedFilter,
    readListCount,
    readPageIds,
    readPageNr,
    readSelectedIds,
} from 'components/keywordMappings/keywordMappingBulk'
import KeywordMappingBulkBar from 'components/keywordMappings/KeywordMappingBulkBar'
import KeywordMappingBulkModal from 'components/keywordMappings/KeywordMappingBulkModal'
import KeywordMappingFormModal from 'components/keywordMappings/KeywordMappingFormModal'
import KeywordMappingImportModal from 'components/keywordMappings/KeywordMappingImportModal'
import KeywordMappingImports from 'components/keywordMappings/KeywordMappingImports'
import {
    INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS,
    INVALIDATE_KEYWORD_MAPPINGS,
    KEYWORD_MAPPING_FOCUS_RING,
    KEYWORD_MAPPINGS_STORE,
    readFieldErrors,
    readGenericError,
    readServiceIds,
    readStringList,
} from 'components/keywordMappings/keywordMappingModel'
import KeywordMappingServiceSelect from 'components/keywordMappings/KeywordMappingServiceSelect'
import { useToast } from 'components/misc/Toaster'
import TableList from 'components/TableList'
import FormattedTime from 'components/ui/FormattedTime'
import { useAppDispatch } from 'hooks/hooks'
import { useListPerCountry } from 'hooks/listHooks'
import { KeywordMapping, KeywordMappingSave, readKeywordMapping } from 'interfaces/KeywordMapping'
import { readKeywordMappingImport } from 'interfaces/KeywordMappingImport'
import { hasAccess } from 'misc/user'
import { schema } from 'normalizr'
import queryString from 'query-string'
import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react'

const useKeywordMappingsList = (filterId = 'all') =>
    useListPerCountry(
        filterId,
        {
            url: 'office/keyword-mappings/get-list',
            schema: [
                new schema.Entity(
                    KEYWORD_MAPPINGS_STORE,
                    {},
                    { idAttribute: 'keyword_mapping_id' },
                ),
            ],
            reducerKey: KEYWORD_MAPPINGS_STORE,
        },
        {
            sorting: ['keyword', 'ASC'],
            countList: true,
        },
    )

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null
}

function activeLabel(active: boolean): string {
    if (active === true) return 'Active'
    return 'Inactive'
}

function readMaxFileBytes(value: unknown): number | null {
    if (typeof value !== 'number') return null
    if (Number.isFinite(value) === false) return null
    if (value < 1) return null
    return value
}

function buildKeywordMappingExportHref(filter: KeywordMappingListFilter): string {
    const target = buildKeywordMappingFilter(filter)
    const params: Record<string, string | string[]> = {}
    if (target.countries !== undefined) params.countries = target.countries
    if (target.serviceIds !== undefined) {
        params.serviceIds = target.serviceIds.map((serviceId) => String(serviceId))
    }
    if (target.keyword !== undefined) params.keyword = target.keyword
    if (target.searchIntent !== undefined) params.searchIntent = target.searchIntent
    if (target.active !== undefined) params.active = target.active

    const query = queryString.stringify(params, { arrayFormat: 'bracket' })
    const base = `${window.location.origin}/api/office/keyword-mappings/export`
    if (query === '') return base
    return `${base}?${query}`
}

function renderCell(key: string, item: KeywordMapping): ReactNode {
    if (key === 'active') return activeLabel(item.active)
    if (key === 'modified') {
        if (item.modified === '') return ''
        return <FormattedTime date={item.modified} format="d MMM yyyy, HH:mm" />
    }
    if (key === 'modifiedByName') {
        if (item.modifiedByName === null) return ''
        return item.modifiedByName
    }

    const value = item[key as keyof KeywordMapping]
    if (typeof value === 'string' || typeof value === 'number') return value
    return ''
}

function toSaveBody(item: KeywordMapping, active: boolean): KeywordMappingSave {
    return {
        keyword_mapping_id: item.keyword_mapping_id,
        service_id: item.service_id,
        keyword: item.keyword,
        search_intent: item.search_intent,
        h1_text: item.h1_text,
        active,
    }
}

function keywordSelectorLabel(item: KeywordMapping): string {
    if (item.keyword === '') return `Select keyword mapping ${item.keyword_mapping_id}`
    return `Select keyword ${item.keyword}`
}

interface MatchingSelection {
    filter: KeywordMappingBulkFilter
    signature: string
    page: number
}

interface KeywordMappingsListProps {
    filterId?: string
}

const KeywordMappingsList = ({ filterId = 'all' }: KeywordMappingsListProps) => {
    const dispatch = useAppDispatch()
    const { addToast } = useToast()
    const hasWriteAccess = Boolean(hasAccess('keyword_mappings', 'w'))
    const listState = useKeywordMappingsList(filterId)
    const {
        entities,
        extra: { definitionsKeywordMappings = {}, h1Placeholders = [], importMaxFileBytes } = {},
        filter,
        changeFilter,
        select,
        currentPage,
        count,
        isFetching,
    } = listState
    const [pendingId, setPendingId] = useState<number | null>(null)
    const [importRefreshKey, setImportRefreshKey] = useState(0)
    const [watchImportId, setWatchImportId] = useState<number | null>(null)
    const [matchingSelection, setMatchingSelection] = useState<MatchingSelection | null>(null)
    const maxFileBytes = readMaxFileBytes(importMaxFileBytes)
    const placeholders = readStringList(h1Placeholders)
    const appliedKeyword = readAppliedFilter(filter, 'keyword')
    const appliedSearchIntent = readAppliedFilter(filter, 'searchIntent')
    const [keywordDraft, setKeywordDraft] = useState(appliedKeyword)
    const [searchIntentDraft, setSearchIntentDraft] = useState(appliedSearchIntent)
    const appliedFilter = buildKeywordMappingFilter(filter)
    const filterSignature = keywordMappingFilterSignature(appliedFilter)
    const pageNr = readPageNr(filter.page)
    const pageIds = readPageIds(currentPage)
    const selectedIds = readSelectedIds(filter.selection)
    const selectionSignatureRef = useRef(filterSignature)
    const listCount = readListCount(count)

    useEffect(() => {
        setKeywordDraft(appliedKeyword)
    }, [appliedKeyword])

    useEffect(() => {
        setSearchIntentDraft(appliedSearchIntent)
    }, [appliedSearchIntent])

    useEffect(() => {
        if (filter.countList === true) return
        if (typeof filter.filterId !== 'string') return
        changeFilter({ countList: true, didInvalidate: true })
    }, [filter.countList, filter.filterId])

    useEffect(() => {
        if (matchingSelection === null) return
        if (matchingSelection.signature === filterSignature && matchingSelection.page === pageNr) {
            return
        }
        setMatchingSelection(null)
    }, [matchingSelection, filterSignature, pageNr])

    useEffect(() => {
        if (filter.didInvalidate !== true) return
        setMatchingSelection(null)
    }, [filter.didInvalidate])

    // Ticked rows survive paging; the signature leaves out page and sort.
    useEffect(() => {
        if (selectionSignatureRef.current === filterSignature) return
        selectionSignatureRef.current = filterSignature
        if (selectedIds.length > 0) select([])
    }, [filterSignature])

    const entityMap =
        isRecord(entities) && isRecord(entities.keywordMappings) ? entities.keywordMappings : {}
    const tableDefinitions = hasWriteAccess
        ? {
              ...definitionsKeywordMappings,
              actions: { name: 'Actions', noOrder: true, preventRedirect: true },
          }
        : definitionsKeywordMappings
    const statusValue = (() => {
        if (filter.active === '1') return 1
        if (filter.active === '0') return 0
        return null
    })()
    const isPending = pendingId !== null
    // A new filter reaches the store only after its fetch, so until then the selection is the old one.
    const isListLoading = isFetching === true || isFetching === filterId
    const bulkTarget = ((): KeywordMappingBulkTarget | null => {
        if (matchingSelection !== null) return { filter: matchingSelection.filter }
        if (selectedIds.length === 0) return null
        return { ids: selectedIds }
    })()
    const selectedCount = ((): number => {
        if (matchingSelection !== null && listCount !== null) return listCount
        return selectedIds.length
    })()
    const offerSelectAllCount = ((): number | null => {
        if (matchingSelection !== null) return null
        if (listCount === null) return null
        if (isPageFullySelected(pageIds, selectedIds) === false) return null
        if (listCount > pageIds.length) return listCount
        return null
    })()

    const applyTextFilter = (field: 'keyword' | 'searchIntent', value: string) => {
        const next = value.trim()
        const current = readAppliedFilter(filter, field)
        if (next === current) return
        changeFilter({ [field]: next === '' ? undefined : next, page: 0 })
    }

    const onStatusChange = ({ target: { value } }: SelectSimpleChangeEvent) => {
        if (value === null) {
            changeFilter({ active: undefined, page: 0 })
            return
        }
        changeFilter({ active: value === 0 ? '0' : '1', page: 0 })
    }

    const selectKeywordMappings = (ids: number | number[]) => {
        setMatchingSelection(null)
        select(ids)
    }

    const onSelectAllMatching = () => {
        setMatchingSelection({
            filter: appliedFilter,
            signature: filterSignature,
            page: pageNr,
        })
    }

    const onClearSelection = () => {
        setMatchingSelection(null)
        select([])
    }

    const openForm = (mapping?: KeywordMapping) => {
        if (isPending) return
        void NiceModal.show(KeywordMappingFormModal, { mapping, h1Placeholders: placeholders })
    }

    const openBulk = (action: KeywordMappingBulkAction) => {
        if (isPending || isListLoading) return
        if (bulkTarget === null) return
        void NiceModal.show(KeywordMappingBulkModal, {
            action,
            target: bulkTarget,
            h1Placeholders: placeholders,
        }).catch(() => undefined)
    }

    const openImport = () => {
        if (maxFileBytes === null) return
        void NiceModal.show(KeywordMappingImportModal, { maxFileBytes })
            .then((result: unknown) => {
                setImportRefreshKey((current) => current + 1)
                const imported = readKeywordMappingImport(result)
                if (imported === null) return
                setWatchImportId(imported.keyword_mapping_import_id)
            })
            .catch(() => undefined)
    }

    const exportHref = buildKeywordMappingExportHref(filter)

    const runAction = async (id: number, request: () => Promise<unknown>) => {
        setPendingId(id)
        try {
            await request()
            dispatch({ type: INVALIDATE_KEYWORD_MAPPINGS })
            dispatch({ type: INVALIDATE_KEYWORD_MAPPING_SEARCH_INTENTS })
        } catch (response: unknown) {
            const errors = readFieldErrors(response)
            const fieldMessage = Object.values(errors)[0]
            addToast({
                msg:
                    fieldMessage ??
                    readGenericError(response, 'The keyword mapping could not be updated.'),
                red: true,
            })
        } finally {
            setPendingId(null)
        }
    }

    const onToggle = (item: KeywordMapping) => {
        const nextActive = item.active === false
        void runAction(item.keyword_mapping_id, () =>
            call('office/keyword-mappings/save', { json: [toSaveBody(item, nextActive)] }),
        )
    }

    const onDelete = (item: KeywordMapping) => {
        const confirmed = window.confirm(`Delete the keyword mapping for "${item.keyword}"?`)
        if (confirmed === false) return
        void runAction(item.keyword_mapping_id, () =>
            call(`office/keyword-mappings/delete/${item.keyword_mapping_id}`, { method: 'POST' }),
        )
    }

    const renderRow = (id: number) => {
        const item = readKeywordMapping(entityMap[id])
        if (item === null) {
            return {
                cols: Object.keys(tableDefinitions).map(() => ''),
                selectorLabel: `Select keyword mapping ${id}`,
            }
        }

        const onRowClick = (event: MouseEvent<HTMLElement>) => {
            const target = event.target
            if (target instanceof Element && target.closest('button, a, input, select, textarea')) {
                return
            }
            openForm(item)
        }

        return {
            cols: Object.keys(tableDefinitions).map((key) => {
                if (key !== 'actions') return renderCell(key, item)

                const toggleLabel = item.active === true ? 'Deactivate' : 'Activate'
                return (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            name="text"
                            disabled={isPending}
                            onClick={() => openForm(item)}>
                            Edit
                        </Button>
                        <Button
                            type="button"
                            name="text"
                            disabled={isPending}
                            onClick={() => onToggle(item)}>
                            {toggleLabel}
                        </Button>
                        <Button
                            type="button"
                            name="text"
                            disabled={isPending}
                            onClick={() => onDelete(item)}>
                            Delete
                        </Button>
                    </div>
                )
            }),
            onClick: hasWriteAccess ? onRowClick : undefined,
            className: hasWriteAccess ? 'cursor-pointer' : undefined,
            selectorLabel: keywordSelectorLabel(item),
        }
    }

    return (
        <>
            <div className="mb-4 flex flex-wrap items-end gap-4">
                <div className="w-64">
                    <KeywordMappingServiceSelect
                        name="serviceIds"
                        emptyOption="All services"
                        value={readServiceIds(filter.serviceIds)[0] ?? null}
                        onChange={(serviceId) =>
                            changeFilter({
                                serviceIds: serviceId === null ? [] : [serviceId],
                                page: 0,
                            })
                        }
                    />
                </div>
                <InputField
                    id="keyword-mapping-filter-keyword"
                    name="keyword"
                    className="w-64"
                    legend="Keyword"
                    aria-label="Keyword"
                    value={keywordDraft}
                    onChange={(event) => setKeywordDraft(event.target.value)}
                    onBlur={() => applyTextFilter('keyword', keywordDraft)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') applyTextFilter('keyword', keywordDraft)
                    }}
                />
                <InputField
                    id="keyword-mapping-filter-search-intent"
                    name="searchIntent"
                    className="w-64"
                    legend="Search intent"
                    aria-label="Search intent"
                    value={searchIntentDraft}
                    onChange={(event) => setSearchIntentDraft(event.target.value)}
                    onBlur={() => applyTextFilter('searchIntent', searchIntentDraft)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            applyTextFilter('searchIntent', searchIntentDraft)
                        }
                    }}
                />
                <div className="w-48">
                    <SelectSimple
                        alwaysControlValue
                        name="active"
                        placeholder="Status"
                        emptyOption="All statuses"
                        value={statusValue}
                        options={[
                            { name: 'Active', value: 1 },
                            { name: 'Inactive', value: 0 },
                        ]}
                        onChange={onStatusChange}
                    />
                </div>
                {hasWriteAccess && (
                    <Button
                        type="button"
                        appearance="primary"
                        disabled={isPending}
                        onClick={() => openForm()}>
                        New keyword mapping
                    </Button>
                )}
                {hasWriteAccess && (
                    <Button
                        type="button"
                        appearance="secondary"
                        className={KEYWORD_MAPPING_FOCUS_RING}
                        disabled={maxFileBytes === null}
                        onClick={openImport}>
                        Import CSV
                    </Button>
                )}
                <a
                    href={exportHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1 rounded border border-solid border-[#9ca5ad] bg-base-white px-[31px] py-2 font-[family-name:var(--font-body-default,Poppins,'Work_Sans',Helvetica,Arial,serif)] text-sm font-bold leading-[22px] text-inherit no-underline hover:bg-[#edeef0] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5a8bfc]">
                    Export CSV
                </a>
            </div>
            <KeywordMappingImports refreshKey={importRefreshKey} watchImportId={watchImportId} />
            {hasWriteAccess && bulkTarget !== null && (
                <KeywordMappingBulkBar
                    selectedCount={selectedCount}
                    matchingFilter={matchingSelection !== null}
                    offerSelectAllCount={offerSelectAllCount}
                    disabled={isPending || isListLoading}
                    onSetH1={() => openBulk('h1')}
                    onActivate={() => openBulk('activate')}
                    onDeactivate={() => openBulk('deactivate')}
                    onDelete={() => openBulk('delete')}
                    onSelectAllMatching={onSelectAllMatching}
                    onClear={onClearSelection}
                />
            )}
            <TableList
                {...listState}
                select={selectKeywordMappings}
                selectable={hasWriteAccess}
                headerSelectorLabel="Select all keywords on this page"
                definitions={tableDefinitions}
                renderRow={renderRow}
                addListFeatures
                hideSearch
                hideExport
            />
        </>
    )
}

export default KeywordMappingsList
